/**
 * OnboardingModal - First-time user onboarding wizard
 * 3-step flow: Team Selection -> Role Selection -> Hub Organization
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { TEAM_OPTIONS, ROLE_OPTIONS, HUB_ORGANIZATION_OPTIONS } from '../constants/onboardingOptions';
import garageAPI from '../../../services/garage/api';
import './OnboardingModal.css';

const OnboardingModal = ({ isOpen, onClose, onSave, onSkip, userEmail, userName, configVersion = 0 }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  // Department comes from Workday and is admin-managed — shown read-only
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [workdayBusinessUnit, setWorkdayBusinessUnit] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  // Dynamic options
  const [dynamicRoles, setDynamicRoles] = useState({ leadership: [], operations: [] });
  const [dynamicTeams, setDynamicTeams] = useState([]);
  const [dynamicDepartments, setDynamicDepartments] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);

  const departmentLabel =
    dynamicDepartments.find((d) => d.id === selectedDepartment)?.label ||
    selectedDepartment ||
    workdayBusinessUnit ||
    'Not mapped yet';

  // Load dynamic options on mount or when config changes
  useEffect(() => {
    const loadDynamicOptions = async () => {
      setRolesLoading(true);
      try {
        const [roleOptions, teamOptions, departmentOptions, workdayHint] = await Promise.all([
          garageAPI.getConfigOptions('user_profile_role'),
          garageAPI.getConfigOptions('team'),
          garageAPI.getConfigOptions('department'),
          garageAPI.getMyWorkdayDepartment().catch(() => ({ businessUnit: null })),
        ]);

        // Group roles by category_type (leadership/operations)
        const leadership = roleOptions
          .filter(r => r.categoryType === 'leadership')
          .map(r => ({ id: r.value, label: r.label }));

        const operations = roleOptions
          .filter(r => r.categoryType === 'operations')
          .map(r => ({ id: r.value, label: r.label }));

        setDynamicRoles({ leadership, operations });
        setDynamicTeams(teamOptions.map(t => ({ id: t.value, label: t.label })));
        const depts = (departmentOptions || []).map((d) => ({
          id: d.value,
          label: d.label || d.value,
        }));
        setDynamicDepartments(depts);

        const bu = (workdayHint?.businessUnit || '').trim();
        if (bu) {
          setWorkdayBusinessUnit(bu);
          const match = depts.find(
            (d) =>
              d.id.toLowerCase() === bu.toLowerCase() ||
              d.label.toLowerCase() === bu.toLowerCase()
          );
          if (match) setSelectedDepartment(match.id);
        }
      } catch (error) {
        console.error('Failed to load dynamic options:', error);
        // Fallback to hardcoded options
        setDynamicRoles(ROLE_OPTIONS);
        setDynamicTeams(TEAM_OPTIONS);
        setDynamicDepartments([]);
      } finally {
        setRolesLoading(false);
      }
    };

    if (isOpen) {
      loadDynamicOptions();
    }
  }, [isOpen, configVersion]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setFirstName('');
      setLastName('');
      setSelectedTeam(null);
      setSelectedDepartment(null);
      setWorkdayBusinessUnit(null);
      setSelectedRole(null);
      setSelectedOrganization(null);
    }
  }, [isOpen]);

  const handleSkip = useCallback(() => {
    if (onSkip) {
      onSkip();
    }
    onClose();
  }, [onSkip, onClose]);

  // Handle ESC key to skip
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleSkip();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleSkip]);

  if (!isOpen) return null;

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSave = () => {
    const preferences = {
      firstName,
      lastName,
      persona: selectedTeam,
      userRole: selectedRole,
      hubOrganization: selectedOrganization,
    };
    onSave(preferences);
    onClose();
  };

  const handleTeamSelect = (teamId) => {
    setSelectedTeam(teamId);
  };

  const handleRoleSelect = (roleId) => {
    setSelectedRole(roleId);
  };

  const handleOrganizationSelect = (orgId) => {
    setSelectedOrganization(orgId);
  };

  const handleClear = () => {
    if (currentStep === 0) {
      setFirstName('');
      setLastName('');
    }
    if (currentStep === 1) setSelectedTeam(null);
    if (currentStep === 2) setSelectedRole(null);
    if (currentStep === 3) setSelectedOrganization(null);
  };

  const renderStep0 = () => (
    <div className="onboarding-step">
      <div className="onboarding-step-header">
        <div className="onboarding-step-number">0</div>
        <div className="onboarding-step-content">
          <h3>What's your name?</h3>
          <p className="onboarding-step-subtitle">
            Help us personalize your experience.{' '}
            <span className="optional-badge">OPTIONAL</span>
          </p>
        </div>
        <button className="onboarding-clear-btn" onClick={handleClear}>
          Clear
        </button>
      </div>
      <div className="onboarding-name-inputs">
        <div className="onboarding-name-field">
          <label htmlFor="firstName">First Name</label>
          <input
            id="firstName"
            type="text"
            placeholder="John"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="onboarding-text-input"
          />
        </div>
        <div className="onboarding-name-field">
          <label htmlFor="lastName">Last Name</label>
          <input
            id="lastName"
            type="text"
            placeholder="Doe"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="onboarding-text-input"
          />
        </div>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="onboarding-step">
      <div className="onboarding-step-header">
        <div className="onboarding-step-number">1</div>
        <div className="onboarding-step-content">
          <h3>Which team are you on?</h3>
          <p className="onboarding-step-subtitle">
            We'll surface tools your team uses most.{' '}
            <span className="optional-badge">OPTIONAL</span>
          </p>
        </div>
        <button className="onboarding-clear-btn" onClick={handleClear}>
          Clear
        </button>
      </div>
      <div className="onboarding-options-grid">
        {(dynamicTeams.length > 0 ? dynamicTeams : TEAM_OPTIONS).map((team) => {
          const IconComponent = team.icon;
          return (
            <button
              key={team.id}
              className={`onboarding-option-card ${selectedTeam === team.id ? 'selected' : ''}`}
              onClick={() => handleTeamSelect(team.id)}
              disabled={rolesLoading}
            >
              <div className="option-icon">
                {IconComponent && <IconComponent size={24} />}
              </div>
              <div className="option-label">{team.label}</div>
              {team.description && <div className="option-description">{team.description}</div>}
            </button>
          );
        })}
      </div>
      {workdayBusinessUnit && (
        <div className="onboarding-department-block" style={{ marginTop: 24 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
            Department
          </label>
          <input
            className="onboarding-text-input"
            value={departmentLabel}
            readOnly
            disabled
            style={{ width: '100%' }}
          />
          <p className="onboarding-step-subtitle" style={{ marginTop: 8 }}>
            Taken from Workday ({workdayBusinessUnit}). Contact an admin to change it.
          </p>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="onboarding-step">
      <div className="onboarding-step-header">
        <div className="onboarding-step-number">2</div>
        <div className="onboarding-step-content">
          <h3>What's your role?</h3>
          <p className="onboarding-step-subtitle">
            Optional. Tools your peers use most show up first.{' '}
            <span className="optional-badge">OPTIONAL</span>
          </p>
        </div>
        <button className="onboarding-clear-btn" onClick={handleClear}>
          Clear
        </button>
      </div>
      <div className="onboarding-role-sections">
        {dynamicRoles.leadership.length > 0 && (
          <div className="onboarding-role-section">
            <h4>Leadership</h4>
            <div className="onboarding-role-grid">
              {dynamicRoles.leadership.map((role) => (
                <button
                  key={role.id}
                  className={`onboarding-role-card ${selectedRole === role.id ? 'selected' : ''}`}
                  onClick={() => handleRoleSelect(role.id)}
                  disabled={rolesLoading}
                >
                  {role.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {dynamicRoles.operations.length > 0 && (
          <div className="onboarding-role-section">
            <h4>Operations</h4>
            <div className="onboarding-role-grid">
              {dynamicRoles.operations.map((role) => (
                <button
                  key={role.id}
                  className={`onboarding-role-card ${selectedRole === role.id ? 'selected' : ''}`}
                  onClick={() => handleRoleSelect(role.id)}
                  disabled={rolesLoading}
                >
                  {role.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="onboarding-step">
      <div className="onboarding-step-header">
        <div className="onboarding-step-number">3</div>
        <div className="onboarding-step-content">
          <h3>How should we organize your Hub?</h3>
          <p className="onboarding-step-subtitle">
            You can switch this any time with the Group dropdown.{' '}
            <span className="optional-badge">OPTIONAL</span>
          </p>
        </div>
        <button className="onboarding-clear-btn" onClick={handleClear}>
          Clear
        </button>
      </div>
      <div className="onboarding-options-grid-vertical">
        {HUB_ORGANIZATION_OPTIONS.map((option) => {
          const IconComponent = option.icon;
          return (
            <button
              key={option.id}
              className={`onboarding-organization-card ${
                selectedOrganization === option.id ? 'selected' : ''
              } ${option.highlight ? 'highlighted' : ''}`}
              onClick={() => handleOrganizationSelect(option.id)}
            >
              <div className="organization-icon">
                <IconComponent size={24} />
              </div>
              <div className="organization-content">
                <div className="organization-label">{option.label}</div>
                <div className="organization-description">{option.description}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="onboarding-modal-overlay" onClick={handleSkip}>
      <div className="onboarding-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="onboarding-modal-header">
          <div className="onboarding-brand">
            <div className="onboarding-brand-icon">R</div>
            <div className="onboarding-brand-text">
              <span className="brand-name">AI Garage</span>
              <span className="brand-sub">AI Apps & Tools</span>
            </div>
          </div>
          <button className="onboarding-close-btn" onClick={handleSkip} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {/* Title */}
        <div className="onboarding-title-section">
          <h1>Make Ringo yours.</h1>
          <p className="onboarding-subtitle">
            Four optional choices. Fill what you want, skip what you don't — Ringo works either way.
            You can change everything later in Settings.
          </p>
        </div>

        {/* Body - Dynamic based on current step */}
        <div className="onboarding-body">
          {currentStep === 0 && renderStep0()}
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </div>

        {/* Footer */}
        <div className="onboarding-footer">
          <button className="onboarding-skip-btn" onClick={handleSkip}>
            Skip — I'll explore on my own
          </button>

          <div className="onboarding-navigation">
            {currentStep > 0 && (
              <button className="onboarding-back-btn" onClick={handleBack}>
                Back
              </button>
            )}
            {currentStep < 3 ? (
              <button className="onboarding-next-btn" onClick={handleNext}>
                Next
              </button>
            ) : (
              <button className="onboarding-save-btn" onClick={handleSave}>
                Save my preferences →
              </button>
            )}
          </div>
        </div>

        {/* Step indicator */}
        <div className="onboarding-step-indicator">
          <span className="step-dots">
            {[0, 1, 2, 3].map((step) => (
              <span
                key={step}
                className={`step-dot ${currentStep === step ? 'active' : ''} ${
                  currentStep > step ? 'completed' : ''
                }`}
                onClick={() => setCurrentStep(step)}
              />
            ))}
          </span>
          <span className="step-text">Step {currentStep + 1} of 4</span>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
